/**
 * Invoice Generator Component
 * 
 * Modal that triggers invoice creation from a completed job.
 * Calls RPC to generate invoice and displays preview.
 * Frontend remains dumb - all logic in Supabase.
 */

import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { FileText, Loader2, X, AlertCircle, ArrowRight } from 'lucide-react';
import InvoicePreview from './InvoicePreview';
import PaymentModal from './PaymentModal';
import { errorMessage, reportError } from '../../../lib/errors';
import { strike } from '../../../lib/haptics';
import { invoiceReadiness, type InvoiceLineIssue } from '../Workshop/jobWorkflow';

interface InvoiceGeneratorProps {
    jobId: string;
    isOpen: boolean;
    onClose: () => void;
    existingInvoiceId?: string | null;
}

type Stage = 'confirm' | 'generating' | 'preview' | 'error';

export default function InvoiceGenerator({ jobId, isOpen, onClose, existingInvoiceId }: InvoiceGeneratorProps) {
    const [stage, setStage] = useState<Stage>('confirm');
    const [invoiceId, setInvoiceId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [paymentInvoice, setPaymentInvoice] = useState<any>(null);
    const [paymentMode, setPaymentMode] = useState<'card' | 'cash' | 'part'>('card');
    // The job's lines, checked before anything is generated: "+ Part" and
    // "+ Labour" save placeholder lines that must not reach an invoice
    // untouched (jobWorkflow.invoiceReadiness). This is the one path every
    // job invoice takes — the job card and the dashboard's "Invoice →" both
    // open it — so the check lives here.
    // Keyed by job, so a result for one job can never show for another and
    // nothing has to be reset inside the effect.
    const [checked, setChecked] = useState<{
        jobId: string;
        result: { blocking: InvoiceLineIssue[]; warnings: InvoiceLineIssue[] };
        failed: boolean;
    } | null>(null);
    const [acceptedFor, setAcceptedFor] = useState<string | null>(null);
    const readiness = checked?.jobId === jobId ? checked.result : null;
    const readinessFailed = checked?.jobId === jobId && checked.failed;
    const warningsAccepted = acceptedFor === jobId;
    const setWarningsAccepted = (on: boolean) => setAcceptedFor(on ? jobId : null);

    useEffect(() => {
        if (!isOpen || existingInvoiceId || !jobId) return;
        let cancelled = false;
        (async () => {
            const { data, error: linesError } = await supabase
                .from('job_items')
                .select('item_type, description, quantity, unit_price')
                .eq('job_id', jobId);
            if (cancelled) return;
            if (linesError) {
                // Never block invoicing on a failed read; say the lines were
                // not checked instead.
                reportError(linesError, 'invoice-generator.job_items');
                setChecked({ jobId, result: { blocking: [], warnings: [] }, failed: true });
                return;
            }
            setChecked({
                jobId,
                failed: false,
                result: invoiceReadiness((data ?? []).map((l) => ({
                    type: l.item_type,
                    description: l.description,
                    quantity: l.quantity,
                    price: l.unit_price,
                }))),
            });
        })();
        return () => { cancelled = true; };
    }, [isOpen, existingInvoiceId, jobId]);

    const canGenerate = !!readiness && readiness.blocking.length === 0
        && (readiness.warnings.length === 0 || warningsAccepted);

    useEffect(() => {
        if (isOpen) {
            if (existingInvoiceId) {
                setStage('preview');
                setInvoiceId(existingInvoiceId);
            } else {
                setStage('confirm');
                setInvoiceId(null);
            }
        }
    }, [isOpen, existingInvoiceId]);

    if (!isOpen) return null;

    const handleGenerate = async () => {
        setStage('generating');
        setError(null);

        try {
            const { data, error: rpcError } = await supabase.rpc('fn_generate_invoice_from_job', {
                p_job_id: jobId
            });

            if (rpcError) throw rpcError;

            if (!data) throw new Error('No invoice ID returned');

            setInvoiceId(data);
            strike('land');
            setStage('preview');

        } catch (err) {
            setError(errorMessage(err) || 'Failed to generate invoice');
            setStage('error');
        }
    };

    const handlePaymentClick = (invoice: any, mode: 'card' | 'cash' | 'part') => {
        setPaymentMode(mode);
        setPaymentInvoice(invoice);
    };

    const handlePaymentSuccess = () => {
        setPaymentInvoice(null);
        // Refresh invoice preview by toggling ID
        const currentId = invoiceId;
        setInvoiceId(null);
        setTimeout(() => setInvoiceId(currentId), 100);
    };

    const handleClose = () => {
        setStage('confirm');
        setInvoiceId(null);
        setError(null);
        // Forget the line check: the lines may change before this reopens,
        // and a stale "all clear" must never enable Generate.
        setChecked(null);
        setAcceptedFor(null);
        onClose();
    };

    return (
        <>
            <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-scrim/70 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto">
                <div className="bg-panel border border-tint/10 rounded-none sm:rounded-xl w-full max-w-4xl min-h-dvh sm:min-h-0 sm:max-h-[90vh] overflow-y-auto animate-in zoom-in-95">
                    {/* Header */}
                    <div className="sticky top-0 z-10 px-4 sm:px-6 py-4 bg-panel border-b border-tint/10 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-accent" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-fg">
                                    {stage === 'preview' ? 'Invoice Preview' : 'Generate Invoice'}
                                </h2>
                                <p className="text-sm text-fg-3">
                                    {stage === 'preview' ? 'Review and send to customer' : 'Create invoice from job items'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 text-fg-3 hover:text-fg hover:bg-tint/10 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-3 sm:p-6">
                        {/* Confirm Stage */}
                        {stage === 'confirm' && (
                            <div className="text-center py-8">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/10 flex items-center justify-center">
                                    <FileText className="w-10 h-10 text-accent" />
                                </div>
                                <h3 className="text-xl font-bold text-fg mb-2">
                                    {readiness && readiness.blocking.length > 0 ? 'Finish the job lines first' : 'Ready to Generate Invoice?'}
                                </h3>
                                <p className="text-fg-3 mb-6 max-w-md mx-auto">
                                    {readiness && readiness.blocking.length > 0
                                        ? 'Some lines were added but never filled in. Complete them on the job card, then generate the invoice.'
                                        : 'This will create a professional invoice from all job items. You can review and edit before sending to the customer.'}
                                </p>

                                {readiness && readiness.blocking.length > 0 && (
                                    <IssueList tone="danger" title="Can't be invoiced yet" issues={readiness.blocking} />
                                )}
                                {readiness && readiness.blocking.length === 0 && readiness.warnings.length > 0 && (
                                    <div className="max-w-md mx-auto mb-6 text-left">
                                        <IssueList tone="warning" title="Check before invoicing" issues={readiness.warnings} />
                                        <label className="mt-3 flex items-start gap-3 min-h-[44px] cursor-pointer text-sm text-fg-2">
                                            <input
                                                type="checkbox"
                                                checked={warningsAccepted}
                                                onChange={(e) => setWarningsAccepted(e.target.checked)}
                                                className="mt-0.5 w-5 h-5 shrink-0"
                                            />
                                            <span>These are correct. Invoice them as they are.</span>
                                        </label>
                                    </div>
                                )}
                                {readinessFailed && (
                                    <p className="text-xs text-fg-4 mb-4">Couldn't check the job lines. Review the invoice before you send it.</p>
                                )}

                                <div className="flex flex-col-reverse sm:flex-row justify-center gap-3">
                                    <button onClick={handleClose} className="fd-btn fd-btn-secondary">
                                        {readiness && readiness.blocking.length > 0 ? 'Back to the job' : 'Cancel'}
                                    </button>
                                    <button
                                        onClick={handleGenerate}
                                        disabled={!canGenerate}
                                        // Brass, the money metal; a recessed well until the
                                        // lines are confirmed, then it lights (fd-btn-ready).
                                        className="fd-btn fd-btn-brass fd-btn-ready fd-btn-lg"
                                    >
                                        {!readiness ? <><Loader2 className="w-4 h-4 animate-spin" /> Checking lines…</> : <>Generate Invoice <ArrowRight className="w-4 h-4" /></>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Generating Stage */}
                        {stage === 'generating' && (
                            <div className="text-center py-16">
                                <Loader2 className="w-12 h-12 text-accent animate-spin mx-auto mb-4" />
                                <p className="text-fg-3">Generating invoice...</p>
                            </div>
                        )}

                        {/* Error Stage */}
                        {stage === 'error' && (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger/20 flex items-center justify-center">
                                    <AlertCircle className="w-8 h-8 text-danger" />
                                </div>
                                <h3 className="text-lg font-bold text-fg mb-2">Generation Failed</h3>
                                <p className="text-danger text-sm mb-6">{error}</p>
                                <button
                                    onClick={() => setStage('confirm')}
                                    className="px-6 py-2 bg-tint/10 rounded-lg font-medium text-fg hover:bg-tint/20 transition-all"
                                >
                                    Try Again
                                </button>
                            </div>
                        )}

                        {/* Preview Stage */}
                        {stage === 'preview' && invoiceId && (
                            <div>
                                {/* No separate "Issue" step: emailing, printing or
                                    downloading the invoice (InvoicePreview) and
                                    taking any payment (fn_record_invoice_payment,
                                    the card checkout) each move a draft to issued. */}
                                <InvoicePreview
                                    invoiceId={invoiceId}
                                    onPaymentClick={handlePaymentClick}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Payment Modal */}
            <PaymentModal
                invoice={paymentInvoice}
                isOpen={!!paymentInvoice}
                onClose={() => setPaymentInvoice(null)}
                onSuccess={handlePaymentSuccess}
                initialMode={paymentMode}
            />
        </>
    );
}

/** The lines that stop, or need a second look before, invoicing. */
function IssueList({ tone, title, issues }: { tone: 'danger' | 'warning'; title: string; issues: InvoiceLineIssue[] }) {
    return (
        <div className={`max-w-md mx-auto mb-4 text-left fd-notice ${tone === 'danger' ? 'fd-notice-danger' : 'fd-notice-warning'}`} role={tone === 'danger' ? 'alert' : undefined}>
            <span className="fd-notice-icon" aria-hidden="true"><AlertCircle /></span>
            <div className="min-w-0">
                <p className="fd-notice-title">{title}</p>
                <ul className="fd-notice-text list-disc pl-4 space-y-0.5">
                    {issues.map((i) => (
                        <li key={`${i.line}-${i.problem}`} className="break-words">
                            <strong>{i.label}</strong> {i.problem}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
